/**
 * DataTable<T> — Generic paginated table component with loading/empty/error states.
 *
 * Renders sortable columns, sticky header, keyboard navigation, and
 * responsive loading shimmer. Uses the result from useListQuery<T>.
 *
 * Dark theme: bg-bg-base, bg-bg-raised borders, hover row bg-bg-raised/50.
 * Keyboard: arrow keys move focus, Enter triggers onRowClick.
 */
import React, { useRef, useEffect } from 'react';
import type { UseListQueryResult } from '@/hooks/useListQuery';

export interface ColumnDef<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  className?: string;
  /**
   * Omit this column from the mobile card layout (`mobileMode="cards"`).
   * Use for control columns that make no sense as a label/value row — e.g. a
   * leading select checkbox or a trailing icon-actions column, which otherwise
   * render as a stray empty-labelled box on the card.
   */
  hideOnCard?: boolean;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  query: UseListQueryResult<T>;
  rowKey: (row: T) => string | number;
  emptyMessage?: string;
  emptyFilteredMessage?: string;
  onRowClick?: (row: T) => void;
  /**
   * How the table adapts below the `md` breakpoint (< 768px):
   * - `'scroll'` (default): keep the table, wrap in a horizontal scroll
   *   region with a minimum width. Best for dense, column-heavy admin tables.
   * - `'cards'`: stack each row into a tappable label/value card. Best for
   *   field-facing lists (voters, routes) used on a phone.
   */
  mobileMode?: 'scroll' | 'cards';
}

/**
 * DataTable component with loading shimmer, empty states, and error handling.
 */
export function DataTable<T>({
  columns,
  query,
  rowKey,
  emptyMessage = 'No records yet.',
  emptyFilteredMessage = 'No results match these filters.',
  onRowClick,
  mobileMode = 'scroll',
}: DataTableProps<T>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [focusedRowIndex, setFocusedRowIndex] = React.useState(0);

  // Determine if any filters are active
  const hasActiveFilters =
    (query.appliedFilters && query.appliedFilters.length > 0) ||
    query.search ||
    Object.keys(query.filters).length > 0;

  // ============================================================================
  // Keyboard navigation
  // ============================================================================
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    rowIndex: number,
    row: T
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rowIndex < query.items.length - 1) {
        setFocusedRowIndex(rowIndex + 1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIndex > 0) {
        setFocusedRowIndex(rowIndex - 1);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  useEffect(() => {
    // Focus the row element when focusedRowIndex changes
    const rows = tableRef.current?.querySelectorAll('tbody tr');
    if (rows && rows[focusedRowIndex]) {
      (rows[focusedRowIndex] as HTMLElement).focus();
    }
  }, [focusedRowIndex]);

  // ============================================================================
  // Sort header component (inline, minimal version)
  // ============================================================================
  const SortHeaderCell = ({ col, index }: { col: ColumnDef<T>; index: number }) => {
    const isActive = query.sort === col.key;
    const isDesc = query.order === 'desc';

    const handleClick = () => {
      if (!col.sortable) return;
      if (isActive) {
        // Toggle direction
        query.setSort(col.key, isDesc ? 'asc' : 'desc');
      } else {
        // New sort, default to desc
        query.setSort(col.key, 'desc');
      }
    };

    return (
      <th
        key={`header-${index}`}
        onClick={handleClick}
        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide border-b border-border-base bg-bg-base ${
          col.sortable
            ? 'cursor-pointer hover:bg-bg-raised hover:text-fg-primary'
            : 'cursor-default'
        } ${col.className || ''} ${
          isActive ? 'text-status-success' : 'text-fg-muted'
        }`}
        aria-sort={
          isActive
            ? isDesc
              ? 'descending'
              : 'ascending'
            : 'none'
        }
      >
        <div className="flex items-center gap-1">
          <span>{col.header}</span>
          {col.sortable && isActive && (
            <span className="ml-1 text-status-success">
              {isDesc ? '↓' : '↑'}
            </span>
          )}
          {col.sortable && !isActive && (
            <span className="ml-1 text-fg-muted text-[0.7rem]">
              ⇅
            </span>
          )}
        </div>
      </th>
    );
  };

  // ============================================================================
  // Loading state: 5 shimmer rows
  // ============================================================================
  if (query.loading && query.items.length === 0) {
    return (
      <div className="bg-bg-raised rounded-lg border border-border-base overflow-clip">
        <table className="w-full">
          <thead className="sticky top-[calc(var(--app-header-h)+var(--filter-bar-h,0px))] z-20">
            <tr className="border-b border-border-base bg-bg-base shadow-ds-sm">
              {columns.map((col, i) => (
                <SortHeaderCell key={i} col={col} index={i} />
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, rowIdx) => (
              <tr
                key={rowIdx}
                className="border-b border-border-base hover:bg-bg-raised/50 animate-pulse"
              >
                {columns.map((col, colIdx) => (
                  <td key={`${rowIdx}-${colIdx}`} className="px-4 py-4">
                    <div className="h-4 bg-bg-elevated rounded w-3/4" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ============================================================================
  // Error state
  // ============================================================================
  if (query.error) {
    const status = (query.error as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      return (
        <div className="bg-bg-raised rounded-lg border border-border-base p-12 text-center">
          <p className="text-fg-primary text-lg font-semibold mb-1">Access restricted</p>
          <p className="text-fg-muted text-sm">
            Your role doesn't have permission to view this data. Contact an administrator if you need access.
          </p>
        </div>
      );
    }
    return (
      <div className="bg-bg-raised rounded-lg border border-status-danger-bg bg-status-danger-bg/20 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-status-danger font-semibold">
              Failed to load data
            </p>
            <p className="text-status-danger text-sm mt-1">
              {query.error.message || 'An error occurred while fetching results.'}
            </p>
          </div>
          <button
            onClick={query.refetch}
            className="ml-4 px-4 py-2 bg-accent hover:bg-accent-hover text-white text-sm font-semibold rounded whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Empty state (no results, no filters)
  // ============================================================================
  if (!query.loading && query.items.length === 0 && !hasActiveFilters) {
    return (
      <div className="bg-bg-raised rounded-lg border border-border-base p-12 text-center">
        <p className="text-fg-secondary text-lg font-medium">{emptyMessage}</p>
      </div>
    );
  }

  // ============================================================================
  // Empty state (no results, with filters)
  // ============================================================================
  if (!query.loading && query.items.length === 0 && hasActiveFilters) {
    return (
      <div className="bg-bg-raised rounded-lg border border-border-base p-12 text-center">
        <p className="text-fg-secondary text-lg font-medium mb-4">
          {emptyFilteredMessage}
        </p>
        <button
          onClick={query.clearAll}
          className="text-status-success hover:text-status-success/80 underline text-sm font-semibold"
        >
          Clear all filters
        </button>
      </div>
    );
  }

  // ============================================================================
  // Normal render
  // ============================================================================
  const cellValue = (col: ColumnDef<T>, row: T): React.ReactNode =>
    col.render ? col.render(row) : (row[col.key as keyof T] as React.ReactNode);

  const tableMarkup = (
    <table
      ref={tableRef}
      // In scroll mode, force a min width below `md` so the row scrolls
      // horizontally instead of crushing columns; harmless on wide screens.
      className={`w-full text-sm ${mobileMode === 'scroll' ? 'min-w-[44rem] md:min-w-0' : ''}`}
      role="table"
      aria-label="Data table"
    >
      <thead className="sticky top-[calc(var(--app-header-h)+var(--filter-bar-h,0px))] z-20">
        <tr className="border-b border-border-base bg-bg-base shadow-ds-sm">
          {columns.map((col, i) => (
            <SortHeaderCell key={i} col={col} index={i} />
          ))}
        </tr>
      </thead>
      <tbody>
        {query.items.map((row, rowIdx) => (
          <tr
            key={rowKey(row)}
            tabIndex={rowIdx === focusedRowIndex ? 0 : -1}
            onKeyDown={(e) => handleKeyDown(e, rowIdx, row)}
            onClick={() => onRowClick?.(row)}
            className={`border-b border-border-base transition-colors ${
              onRowClick ? 'cursor-pointer' : ''
            } ${
              rowIdx === focusedRowIndex
                ? 'bg-bg-elevated/50 ring-2 ring-status-success'
                : 'hover:bg-bg-raised/50'
            }`}
          >
            {columns.map((col) => (
              <td
                key={`${rowKey(row)}-${col.key}`}
                className={`px-4 py-3 text-fg-secondary ${col.className || ''}`}
              >
                {cellValue(col, row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-4">
      {mobileMode === 'cards' ? (
        <>
          {/* Mobile (< md): stacked label/value cards */}
          <ul className="md:hidden space-y-3" role="list">
            {query.items.map((row) => (
              <li key={rowKey(row)}>
                <div
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick?.(row)}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                  className={`bg-bg-raised rounded-lg border border-border-base p-4 space-y-2 ${
                    onRowClick ? 'cursor-pointer active:bg-bg-elevated/60 transition-colors' : ''
                  }`}
                >
                  {columns.filter((col) => !col.hideOnCard).map((col) => (
                    <div
                      key={`${rowKey(row)}-${col.key}`}
                      className="flex items-baseline justify-between gap-3"
                    >
                      <span className="text-xs font-medium uppercase tracking-wide text-fg-muted shrink-0">
                        {col.header}
                      </span>
                      <span className="text-sm text-fg-secondary text-right min-w-0 break-words">
                        {cellValue(col, row)}
                      </span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {/* Desktop (md+): full table */}
          <div className="hidden md:block bg-bg-raised rounded-lg border border-border-base overflow-clip">
            {tableMarkup}
          </div>
        </>
      ) : (
        <div
          className={`bg-bg-raised rounded-lg border border-border-base ${
            mobileMode === 'scroll' ? 'overflow-x-auto md:overflow-clip' : 'overflow-clip'
          }`}
        >
          {tableMarkup}
        </div>
      )}

      {/* Results count and pagination */}
      <ResultsCount query={query} />
    </div>
  );
}

// ============================================================================
// ResultsCount sub-component
// ============================================================================
interface ResultsCountProps<T> {
  query: UseListQueryResult<T>;
}

function ResultsCount<T>({ query }: ResultsCountProps<T>) {
  const start = (query.page - 1) * query.pageSize + 1;
  const end = Math.min(query.page * query.pageSize, query.total);
  const totalPages = Math.ceil(query.total / query.pageSize);

  return (
    <div
      className="flex items-center justify-between flex-wrap gap-2 text-sm text-fg-secondary"
      aria-live="polite"
      aria-atomic="true"
    >
      <span>
        {query.total === 0
          ? 'No results'
          : `Showing ${start}–${end} of ${query.total.toLocaleString()} • Page ${query.page} of ${totalPages}`}
      </span>

      {/* Pagination buttons */}
      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => query.setPage(1)}
            disabled={query.page === 1}
            className="px-2 py-1 min-h-11 min-w-11 inline-flex items-center justify-center bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary rounded text-xs font-semibold"
            title="First page"
          >
            ⟨⟨
          </button>
          <button
            onClick={() => query.setPage(Math.max(1, query.page - 1))}
            disabled={query.page === 1}
            className="px-2 py-1 min-h-11 min-w-11 inline-flex items-center justify-center bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary rounded text-xs font-semibold"
            title="Previous page"
          >
            ⟨
          </button>

          {/* Page number input (optional, for quick jump) */}
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={totalPages}
              value={query.page}
              onChange={(e) => {
                const p = Number(e.target.value) || 1;
                if (p >= 1 && p <= totalPages) {
                  query.setPage(p);
                }
              }}
              className="w-12 px-1.5 py-1 bg-bg-elevated border border-border-subtle rounded text-fg-primary text-xs text-center"
              title="Jump to page"
              aria-label="Jump to page"
            />
            <span className="text-fg-muted text-xs">/ {totalPages}</span>
          </div>

          <button
            onClick={() => query.setPage(Math.min(totalPages, query.page + 1))}
            disabled={query.page === totalPages}
            className="px-2 py-1 min-h-11 min-w-11 inline-flex items-center justify-center bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary rounded text-xs font-semibold"
            title="Next page"
          >
            ⟩
          </button>
          <button
            onClick={() => query.setPage(totalPages)}
            disabled={query.page === totalPages}
            className="px-2 py-1 min-h-11 min-w-11 inline-flex items-center justify-center bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary rounded text-xs font-semibold"
            title="Last page"
          >
            ⟩⟩
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
